import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FirestoreService } from '../../services/firestore.service';
import { take, throwError } from 'rxjs';
import { SubscriptionService } from '../../services/subscription.service';
import { LocationService, CountryInfo, StateInfo, CityInfo } from '../../services/location.service';
import { PromoCode } from '../../models/promo-code.model';
import { isPromoCodeSyncedWithStripe, PROMO_CODE_NOT_SYNCED_WITH_STRIPE_MESSAGE } from '../../services/promo-codes.service';
import { SubscriptionPlansService } from '../../services/subscription-plans.service';
import { SubscriptionPlan } from '../../models/subscription-plan.model';
import { AccountContextService } from '../../services/account-context.service';
import { AccountPayment } from '../../models/payment.model';
import { StripeBillingPreviewResponse, StripeCheckoutHttpError, StripeCheckoutService } from '../../services/stripe-checkout.service';
import { environment } from '../../../environments/environment';
import { AppSettingsService } from '../../services/app-settings.service';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.css'
})
export class CheckoutComponent implements OnInit {
  checkoutForm!: FormGroup;
  submitted = false;
  processing = false;
  userId: string = '';
  isSubscriptionActive: boolean = false;
  profile: any = null;
  selectedPlanLevel: string = '';
  subscriptionId: string | null = null;
  previousSubscriptionId: string | null = null;
  isUpgrade: boolean = false;
  planDetails: SubscriptionPlan | null = null;
  promoCode: string = '';
  appliedPromo: any = null;
  error: string | null = null;
  discount: number = 0;
  cartTotal: number = 0;
  totalAfterDiscount: number = 0;
  userEmail: string = '';
  stripePreview: StripeBillingPreviewResponse | null = null;
  stripePreviewLoading = false;
  stripePreviewError: string | null = null;
  countries: CountryInfo[] = [];
  states: StateInfo[] = [];
  cities: CityInfo[] = [];
  private countriesLoaded?: Promise<void>;
  private stateRequestToken = 0;
  private cityRequestToken = 0;

  private readonly allowedCountryCodes = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
    'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'US', 'AU', 'IN', 'JP', 'NZ', 'TR'
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly firestoreService: FirestoreService,
    private readonly subscriptionService: SubscriptionService,
    private readonly locationService: LocationService,
    private readonly subscriptionPlansService: SubscriptionPlansService,
    private readonly accountContextService: AccountContextService,
    private readonly stripeCheckoutService: StripeCheckoutService,
    readonly appSettingsService: AppSettingsService
  ) {
    this.checkoutForm = this.buildCheckoutForm();
  }

  ngOnInit(): void {
    this.countriesLoaded = this.loadCountries();
    void this.countriesLoaded;
    const navigationState = history.state as {
      selectedPlanLevel?: string;
      subscriptionId?: string;
      existingSubscriptionId?: string | null;
      previousSubscriptionId?: string | null;
      isUpgrade?: boolean;
    };
    this.selectedPlanLevel = navigationState?.selectedPlanLevel ?? this.subscriptionService.getSelectedPlan() ?? '';
    this.subscriptionId = navigationState?.subscriptionId ?? navigationState?.existingSubscriptionId ?? null;
    this.previousSubscriptionId = navigationState?.previousSubscriptionId ?? null;
    this.isUpgrade = navigationState?.isUpgrade === true;

    if (this.selectedPlanLevel) {
      this.subscriptionService.setSelectedPlan(this.selectedPlanLevel);
    } else {
      this.router.navigate(['/subscriptions']);
      return;
    }

    this.subscriptionPlansService.getPlanByLevel(this.selectedPlanLevel).pipe(take(1)).subscribe(plan => {
      this.planDetails = plan;
      this.cartTotal = plan?.price ?? 0;
      this.totalAfterDiscount = this.cartTotal;
      this.loadStripeBillingPreview();
    });
    this.authService.user$.subscribe(user => {
        if (user) {
          this.userId = user.uid;
          this.userEmail = user.email ?? '';
          this.isSubscriptionActive = user.isSubscriptionActive ?? false;
          this.loadStripeBillingPreview();
          this.firestoreService.getUserProfile(user.uid).subscribe(profile => {
            this.profile = profile;
            this.prefillBillingForm();
          });
        }
      });

    this.checkoutForm.get('billingForCompany')?.valueChanges.subscribe(isCompany => {
      this.configureCompanyBillingValidation(isCompany);
    });

    this.configureCompanyBillingValidation(this.checkoutForm.get('billingForCompany')?.value);
  }

  private async loadCountries(): Promise<void> {
    const allowedCountries = new Set(this.allowedCountryCodes);
    const countries = await this.locationService.getCountries();
    this.countries = countries
      .filter(country => allowedCountries.has(country.isoCode))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private buildCheckoutForm(): FormGroup {
    return this.fb.group({
      billingForCompany: [false],
      companyName: [''],
      taxId: [''],
      companyRegistrationNumber: [''],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.email]],
      address: ['', Validators.required],
      address2: [''],
      country: ['', Validators.required],
      state: ['', Validators.required],
      city: ['', Validators.required],
      zip: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9\\-\\s]{4,10}$')]],
      paymentMethod: ['stripe', Validators.required]
    });
  }

  private async prefillBillingForm(): Promise<void> {
    if (!this.checkoutForm) {
      return;
    }

    if (this.countriesLoaded) {
      await this.countriesLoaded;
    }

    const billingInfo = this.profile?.billingInformation || {};
    const nameParts = this.extractNameParts(this.profile?.displayName || '');
    const countryValue = this.findCountryCode(billingInfo.country || '');

    if (countryValue) {
      await this.updateStatesForCountry(countryValue, false);
    }

    const stateValue = countryValue ? this.findStateCode(billingInfo.state || '') : '';

    if (countryValue && stateValue) {
      await this.updateCitiesForState(countryValue, stateValue, false);
    }

    const cityValue = countryValue && stateValue ? this.findCityName(billingInfo.city || '') : '';

    this.checkoutForm.patchValue({
      billingForCompany: !!billingInfo.billingForCompany,
      companyName: billingInfo.companyName || '',
      taxId: billingInfo.taxId || '',
      companyRegistrationNumber: billingInfo.companyRegistrationNumber || '',
      firstName: billingInfo.firstName || this.profile?.firstName || nameParts.firstName || '',
      lastName: billingInfo.lastName || this.profile?.lastName || nameParts.lastName || '',
      email: billingInfo.email || this.profile?.email || '',
      address: billingInfo.address || '',
      address2: billingInfo.address2 || '',
      country: countryValue || '',
      state: stateValue || '',
      city: cityValue || '',
      zip: billingInfo.zip || ''
    });
  }

  onCountryChange(): void {
    const countryCode = this.checkoutForm.get('country')?.value;
    void this.updateStatesForCountry(countryCode, true);
  }

  onStateChange(): void {
    const countryCode = this.checkoutForm.get('country')?.value;
    const stateCode = this.checkoutForm.get('state')?.value;
    void this.updateCitiesForState(countryCode, stateCode, true);
  }

  private async updateStatesForCountry(countryCode: string, resetState: boolean): Promise<void> {
    const requestToken = ++this.stateRequestToken;

    if (resetState) {
      this.checkoutForm.patchValue({ state: '', city: '' });
    }

    this.states = [];
    this.cities = [];

    if (!countryCode) {
      return;
    }

    const states = await this.locationService.getStates(countryCode);

    if (this.stateRequestToken === requestToken) {
      this.states = states.sort((a, b) => a.name.localeCompare(b.name));
      this.cities = [];
    }
  }

  private async updateCitiesForState(countryCode: string, stateCode: string, resetCity: boolean): Promise<void> {
    const requestToken = ++this.cityRequestToken;

    if (resetCity) {
      this.checkoutForm.patchValue({ city: '' });
    }

    if (!countryCode || !stateCode) {
      this.cities = [];
      return;
    }

    const cities = await this.locationService.getCities(countryCode, stateCode);

    if (this.cityRequestToken === requestToken) {
      this.cities = cities.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  onSubmit(): void {
    if (this.processing) {
      return;
    }

    this.submitted = true;
    if (this.checkoutForm.invalid) {
      this.checkoutForm.markAllAsTouched();
      return;
    }

    const promoStripeSyncError = this.getAppliedPromoStripeSyncError();
    if (promoStripeSyncError) {
      this.error = promoStripeSyncError;
      return;
    }

    this.processing = true;
    this.error = null;
    if (!this.planDetails) {
      this.error = 'Selected plan could not be loaded. Please try again.';
      this.processing = false;
      return;
    }

    const schedule = this.buildSubscriptionSchedule(new Date());
    const updatedData = {
      subscriptionLevel: this.selectedPlanLevel,
      subscriptionName: this.planDetails.name,
      subscribedAt: new Date(),
      trialStartsAt: schedule.trialStartsAt,
      trialEndsAt: schedule.trialEndsAt,
      currentPeriodStart: schedule.currentPeriodStart,
      currentPeriodEnd: schedule.currentPeriodEnd,
      expiresAt: schedule.currentPeriodEnd,
      maxBoatsAllowed: this.planDetails.maxBoatsAllowed,
      promoCode: this.appliedPromo ? (this.appliedPromo.id || this.appliedPromo.code) : null,
      totals: {
        subtotal: this.cartTotal,
        discount: this.discount,
        total: this.totalAfterDiscount
      },
      userData: {
        ...this.checkoutForm.value
      }
    };

    this.persistBillingInformation();

    if (this.subscriptionId) {
      this.firestoreService.updateSubscription(this.subscriptionId, {
        planId: this.selectedPlanLevel,
        planName: this.planDetails.name,
        billingPeriod: this.planDetails.billingPeriod,
        status: 'trialing',
        trialStartsAt: schedule.trialStartsAt,
        trialEndsAt: schedule.trialEndsAt,
        currentPeriodStart: schedule.currentPeriodStart,
        currentPeriodEnd: schedule.currentPeriodEnd,
        totals: updatedData.totals,
        billingInformation: updatedData.userData,
        paymentStatus: 'trialing'
      }).subscribe({
        error: err => console.error('Failed to update subscription during checkout:', err)
      });
    }
    this.logUserActivity(this.userId, updatedData);
    const selectedPlanLevel = this.selectedPlanLevel;
    this.subscriptionService.clearSelectedPlan();
    this.createAccountPayment(updatedData).subscribe({
      next: paymentId => {
        if (this.checkoutForm.get('paymentMethod')?.value === 'stripe') {
          if (this.isUpgrade && this.subscriptionId) {
            this.attemptStripeSubscriptionUpgrade(paymentId, selectedPlanLevel);
            return;
          }
          this.startStripeCheckout(paymentId, selectedPlanLevel);
          return;
        }
        this.router.navigate(['/thank-you'], {
          state: { selectedPlanLevel, subscriptionId: this.subscriptionId, paymentId }
        });
      },
      error: err => {
        console.error('Failed to track payment:', err);
        this.error = 'Failed to track payment. Please try again.';
        this.processing = false;
      }
    });
  }

  private attemptStripeSubscriptionUpgrade(paymentId: string, selectedPlanLevel: string): void {
    const promoStripeSyncError = this.getAppliedPromoStripeSyncError();
    if (promoStripeSyncError) {
      this.error = promoStripeSyncError;
      this.processing = false;
      return;
    }

    const accountId = this.accountContextService.activeAccountIdSignal();
    const priceId = this.planDetails?.stripePriceId;

    if (!accountId || !priceId || !this.planDetails || !this.subscriptionId) {
      this.startStripeCheckout(paymentId, selectedPlanLevel);
      return;
    }

    this.stripeCheckoutService.upgradeSubscription({
      accountId,
      userId: this.userId,
      subscriptionId: this.subscriptionId,
      previousSubscriptionId: this.previousSubscriptionId,
      priceId,
      planLevel: selectedPlanLevel,
      planName: this.planDetails.name,
      paymentId,
      prorationBehavior: 'always_invoice',
      promotionCode: this.appliedPromo?.code ?? null
    }).subscribe({
      next: response => {
        this.router.navigate(['/thank-you'], {
          state: {
            selectedPlanLevel,
            subscriptionId: this.subscriptionId,
            paymentId,
            stripeSubscriptionId: response.stripeSubscriptionId,
            upgraded: true
          },
          queryParams: {
            selectedPlanLevel,
            paymentId,
            subscriptionId: this.subscriptionId
          }
        });
      },
      error: err => {
        const error = err as StripeCheckoutHttpError;
        const isMissingMapping = error.status === 404 &&
          (error.message?.includes('No Stripe subscription mapping found yet') ||
           error.message?.toLowerCase().includes('not found'));

        if (isMissingMapping) {
          console.warn('No existing Stripe subscription to upgrade in-place. Falling back to Checkout.', err);
          this.startStripeCheckout(paymentId, selectedPlanLevel);
          return;
        }

        // Stripe forbids changing a subscription's currency. When the new plan
        // is in a different currency, the backend returns 409/currency_mismatch.
        // Fall back to creating a brand-new subscription via Checkout, and tell
        // the backend to cancel the previous Stripe subscription on success so
        // the user isn't billed twice.
        const isCurrencyMismatch = error.status === 409 &&
          (error.code === 'currency_mismatch' ||
           error.message?.toLowerCase().includes('currency'));

        if (isCurrencyMismatch) {
          console.warn(
            'Currency mismatch on in-place upgrade. Falling back to a new Checkout subscription.',
            err
          );
          this.startStripeCheckout(paymentId, selectedPlanLevel, {
            cancelStripeSubscriptionOnSuccess: error.stripeSubscriptionId ?? null,
            previousAppSubscriptionId: this.previousSubscriptionId ?? this.subscriptionId ?? null
          });
          return;
        }

        console.error('Failed to upgrade Stripe subscription in-place:', err);
        const baseMessage = 'Unable to upgrade your subscription. Please try again or contact support.';
        if (!environment.production && err instanceof Error && err.message) {
          this.error = `${baseMessage} Details: ${err.message}`;
        } else {
          this.error = baseMessage;
        }
        this.processing = false;
      }
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.checkoutForm.get(controlName);
    return !!(control && control.invalid && (control.touched || this.submitted));
  }

  applyPromoCode(): void {
    const code = this.promoCode.trim().toUpperCase();
    this.error = null;

    if (!code) {
      this.error = 'Please enter a promo code to apply it.';
      return;
    }

    if (this.appliedPromo && (this.appliedPromo.id === code || this.appliedPromo.code === code)) {
      this.error = 'Promo code already applied.';
      return;
    }

    this.firestoreService.getPromoCode(code).pipe(take(1)).subscribe({
      next: promoCode => {
        const normalized = this.normalizePromoCode(promoCode, code);

        if (!normalized) {
          this.error = 'Promo code does not exist.';
          return;
        }

        const validationError = this.getPromoValidationError(normalized);

        if (validationError) {
          this.error = validationError;
          return;
        }

        this.applyDiscount(normalized);
      },
      error: err => {
        this.error = 'Error checking promo code.';
        console.error(err);
      }
    });
  }

  applyDiscount(promo: any): void {
    this.appliedPromo = promo;
    if (promo.discountType === 'percentage') {
      this.discount = this.cartTotal * (promo.discountValue / 100);
    } else if (promo.discountType === 'fixed') {
      this.discount = promo.discountValue;
    }

    if (this.discount > this.cartTotal) {
      this.discount = this.cartTotal;
    }

    this.totalAfterDiscount = this.cartTotal - this.discount;
    this.loadStripeBillingPreview();
  }



  /**
   * Currency code used for cart and "you pay today" displays. Prefers the
   * Stripe preview's currency (authoritative once we have it), falls back to
   * the plan's configured currency, and finally to EUR (the application's
   * reference currency).
   */
  get stripePreviewCurrency(): string {
    return (
      this.stripePreview?.currency ||
      this.planDetails?.currency ||
      'EUR'
    ).toUpperCase();
  }

  /**
   * Currency for the "Cart line" and "Promo discount" rows, which always
   * reflect the plan's configured currency (the local catalog), not whatever
   * Stripe ultimately settles on.
   */
  get cartCurrency(): string {
    return (this.planDetails?.currency || 'EUR').toUpperCase();
  }

  get displayTotalAmount(): number {
    if (this.checkoutForm.get('paymentMethod')?.value === 'stripe' && this.stripePreview) {
      return this.convertMinorToMajor(this.stripePreview.amount_due_now);
    }

    return this.totalAfterDiscount;
  }

  private loadStripeBillingPreview(): void {
    if (!this.planDetails?.stripePriceId || !this.userId) {
      return;
    }

    const accountId = this.accountContextService.activeAccountIdSignal();
    if (!accountId) {
      return;
    }

    this.stripePreviewLoading = true;
    this.stripePreviewError = null;

    this.requestStripeBillingPreview(accountId, this.subscriptionId, this.previousSubscriptionId);
  }

  private requestStripeBillingPreview(
    accountId: string,
    subscriptionId: string | null,
    previousSubscriptionId: string | null
  ): void {
    if (!this.planDetails?.stripePriceId) {
      this.stripePreviewLoading = false;
      return;
    }

    // The backend resolves the existing Stripe subscription (current,
    // previous, or any other binding for this user/account) on its own and
    // gracefully falls back to a "new subscription" estimate when no
    // binding exists yet. No client-side recursion is necessary, and any
    // failure here just means we show the local cart total as the estimate.
    this.stripeCheckoutService.previewBilling({
      priceId: this.planDetails.stripePriceId,
      accountId,
      userId: this.userId,
      subscriptionId,
      previousSubscriptionId,
      promotionCode: this.appliedPromo?.code ?? null
    }).pipe(take(1)).subscribe({
      next: preview => {
        this.stripePreview = preview;
        this.stripePreviewLoading = false;
      },
      error: err => {
        console.warn('Unable to load Stripe billing preview, using local estimate.', err);
        this.stripePreview = null;
        this.stripePreviewLoading = false;
        this.stripePreviewError = 'Showing local estimate. Final amount and taxes are calculated by Stripe Checkout.';
      }
    });
  }

  private convertMinorToMajor(amountMinor: number): number {
    return amountMinor / 100;
  }

  private normalizePromoCode(promoCode: PromoCode | null, fallbackCode: string): PromoCode | null {
    if (!promoCode) {
      return null;
    }

    return {
      ...promoCode,
      code: promoCode.code?.trim()?.toUpperCase?.() || fallbackCode,
      usedCount: promoCode.usedCount ?? 0,
      minCartValue: promoCode.minCartValue ?? 0,
      oneTimeUse: promoCode.oneTimeUse ?? false,
      allowedUsers: promoCode.allowedUsers ?? []
    };
  }

  private getPromoValidationError(promo: PromoCode): string | null {
    const now = new Date();
    const expiryDate = promo.expiresAt?.toDate ? promo.expiresAt.toDate() : promo.expiresAt;
    const hasExpired = expiryDate ? new Date(expiryDate) < now : false;

    if (!promo.active || hasExpired) {
      return 'Promo code is expired or inactive.';
    }

    if (promo.minCartValue && this.cartTotal < promo.minCartValue) {
      return `Cart total must be at least $${promo.minCartValue} to use this promo code.`;
    }

    if (promo.usageLimit && (promo.usedCount ?? 0) >= promo.usageLimit) {
      return 'Promo code usage limit has been reached.';
    }

    if (promo.oneTimeUse && (promo.usedCount ?? 0) > 0) {
      return 'Promo code has already been used.';
    }

    if (promo.allowedUsers && promo.allowedUsers.length > 0) {
      const normalizedAllowedUsers = promo.allowedUsers.map(user => user.trim().toLowerCase()).filter(Boolean);
      const userEmail = (this.profile?.email || this.userEmail || '').trim().toLowerCase();
      if (!userEmail || !normalizedAllowedUsers.includes(userEmail)) {
        return 'This promo code is not available for your account.';
      }
    }

    if (promo.discountType !== 'percentage' && promo.discountType !== 'fixed') {
      return 'Promo code discount type is invalid.';
    }

    if (!isPromoCodeSyncedWithStripe(promo)) {
      return PROMO_CODE_NOT_SYNCED_WITH_STRIPE_MESSAGE;
    }

    return null;
  }

  getAppliedPromoStripeSyncError(): string | null {
    if (!this.appliedPromo || this.checkoutForm.get('paymentMethod')?.value !== 'stripe') {
      return null;
    }

    return isPromoCodeSyncedWithStripe(this.appliedPromo) ?
      null :
      PROMO_CODE_NOT_SYNCED_WITH_STRIPE_MESSAGE;
  }

  private configureCompanyBillingValidation(isCompanyBilling: boolean): void {
    const companyNameControl = this.checkoutForm.get('companyName');
    const taxIdControl = this.checkoutForm.get('taxId');
    const registrationNumberControl = this.checkoutForm.get('companyRegistrationNumber');

    if (!companyNameControl || !taxIdControl || !registrationNumberControl) {
      return;
    }

    if (isCompanyBilling) {
      companyNameControl.setValidators([Validators.required, Validators.minLength(2)]);
      taxIdControl.setValidators([Validators.required, Validators.minLength(4)]);
      registrationNumberControl.setValidators([Validators.required, Validators.minLength(2)]);
    } else {
      companyNameControl.clearValidators();
      taxIdControl.clearValidators();
      registrationNumberControl.clearValidators();
      companyNameControl.setValue('');
      taxIdControl.setValue('');
      registrationNumberControl.setValue('');
    }

    companyNameControl.updateValueAndValidity();
    taxIdControl.updateValueAndValidity();
    registrationNumberControl.updateValueAndValidity();
  }

  private buildSubscriptionSchedule(start: Date) {
    const trialStartsAt = new Date(start);
    const trialEndsAt = this.addDays(trialStartsAt, 30);
    const currentPeriodStart = new Date(trialEndsAt);
    const currentPeriodEnd = this.addYears(currentPeriodStart, 1);
    return { trialStartsAt, trialEndsAt, currentPeriodStart, currentPeriodEnd };
  }

  private addDays(start: Date, days: number): Date {
    const result = new Date(start);
    result.setDate(result.getDate() + days);
    return result;
  }

  private addYears(start: Date, years: number): Date {
    const result = new Date(start);
    result.setFullYear(result.getFullYear() + years);
    return result;
  }

  private findCountryCode(countryValue: string): string {
    if (!countryValue) {
      return '';
    }

    const trimmedValue = countryValue.trim();
    const upperValue = trimmedValue.toUpperCase();
    const matchedByCode = this.countries.find(country => country.isoCode === upperValue);

    if (matchedByCode) {
      return matchedByCode.isoCode;
    }

    const matchedByName = this.countries.find(country => country.name.toLowerCase() === trimmedValue.toLowerCase());
    return matchedByName?.isoCode ?? '';
  }

  private findStateCode(stateValue: string): string {
    if (!stateValue) {
      return '';
    }

    const trimmedValue = stateValue.trim();
    const upperValue = trimmedValue.toUpperCase();
    const matchedByCode = this.states.find(state => state.isoCode === upperValue);

    if (matchedByCode) {
      return matchedByCode.isoCode;
    }

    const matchedByName = this.states.find(state => state.name.toLowerCase() === trimmedValue.toLowerCase());
    return matchedByName?.isoCode ?? '';
  }

  private findCityName(cityValue: string): string {
    if (!cityValue) {
      return '';
    }

    const trimmedValue = cityValue.trim();
    const matchedByName = this.cities.find(city => city.name.toLowerCase() === trimmedValue.toLowerCase());
    return matchedByName?.name ?? '';
  }

  private extractNameParts(displayName: string): { firstName: string; lastName: string } {
    const trimmed = (displayName || '').trim();
    if (!trimmed) {
      return { firstName: '', lastName: '' };
    }

    const parts = trimmed.split(' ');
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ').trim()
    };
  }

  logUserActivity(userId: string, updatedData: any): void {
    this.firestoreService.logUserActivity(
      userId,
      'checkout submitted',
      {
        subscriptionLevel: updatedData.subscriptionLevel,
        subscriptionName: updatedData.subscriptionName,
        subscribedAt: updatedData.subscribedAt,
        expiresAt: updatedData.expiresAt,
        maxBoatsAllowed: updatedData.maxBoatsAllowed,
        promoCode: updatedData.promoCode,
        totals: updatedData.totals,
        userData: updatedData.userData,
      }
    ).subscribe({
      error: (err) => {
        console.error('Failed to log activity:', err);
      }
    });
  }

  private resolveCheckoutEmail(): string {
    const candidates = [
      this.userEmail,
      this.profile?.email,
      this.checkoutForm.get('email')?.value
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const normalized = candidate.trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return '';
  }

  private resolveCheckoutCustomerName(): string {
    const firstName = this.checkoutForm.get('firstName')?.value;
    const lastName = this.checkoutForm.get('lastName')?.value;
    const formName = [firstName, lastName]
      .filter(value => typeof value === 'string' && value.trim())
      .map(value => value.trim())
      .join(' ');

    const candidates = [
      formName,
      this.profile?.fullName,
      [this.profile?.firstName, this.profile?.lastName].filter(Boolean).join(' '),
      this.profile?.displayName
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const normalized = candidate.trim();
        if (normalized) {
          return normalized;
        }
      }
    }

    return '';
  }

  private persistBillingInformation(): void {
    if (!this.userId) {
      return;
    }

    const billingForm = this.checkoutForm.value;
    const selectedCountry = this.countries.find(country => country.isoCode === billingForm.country);
    const selectedState = this.states.find(state => state.isoCode === billingForm.state);

    const billingInformation = {
      billingForCompany: !!billingForm.billingForCompany,
      companyName: billingForm.companyName?.trim?.() || '',
      taxId: billingForm.taxId?.trim?.() || '',
      companyRegistrationNumber: billingForm.companyRegistrationNumber?.trim?.() || '',
      firstName: billingForm.firstName?.trim?.() || '',
      lastName: billingForm.lastName?.trim?.() || '',
      email: billingForm.email?.trim?.() || this.resolveCheckoutEmail(),
      address: billingForm.address?.trim?.() || '',
      address2: billingForm.address2?.trim?.() || '',
      country: selectedCountry?.name || billingForm.country || '',
      state: selectedState?.name || billingForm.state || '',
      city: billingForm.city?.trim?.() || '',
      zip: billingForm.zip?.trim?.() || ''
    };

    this.firestoreService.updateUserProfile(this.userId, { billingInformation })
      .catch(err => {
        console.error('Failed to persist checkout billing information:', err);
      });
  }

  private createAccountPayment(updatedData: any) {
    const accountId = this.accountContextService.activeAccountIdSignal();
    if (!accountId) {
      this.error = 'Account could not be resolved for payment tracking.';
      this.processing = false;
      return throwError(() => new Error('Missing account id for payment tracking.'));
    }

    const checkoutEmail = this.resolveCheckoutEmail();

    const paymentPayload: AccountPayment = {
      status: 'pending',
      subtotal: this.cartTotal,
      discount: this.discount,
      total: this.totalAfterDiscount,
      currency: this.cartCurrency,
      userId: this.userId,
      userEmail: checkoutEmail,
      planLevel: updatedData.subscriptionLevel,
      planName: updatedData.subscriptionName,
      subscriptionId: this.subscriptionId,
      promoCode: updatedData.promoCode ?? null,
      paymentMethod: this.checkoutForm.get('paymentMethod')?.value
    };

    return this.firestoreService.addAccountPayment(accountId, paymentPayload).pipe(take(1));
  }


  private buildStripeBillingDetails() {
    const billingForm = this.checkoutForm.value;
    const selectedCountry = this.countries.find(country => country.isoCode === billingForm.country);
    const selectedState = this.states.find(state => state.isoCode === billingForm.state);
    const billingForCompany = !!billingForm.billingForCompany;

    return {
      billingForCompany,
      name: billingForCompany
        ? (billingForm.companyName?.trim?.() || this.resolveCheckoutCustomerName())
        : this.resolveCheckoutCustomerName(),
      email: billingForm.email?.trim?.() || this.resolveCheckoutEmail(),
      companyName: billingForCompany ? (billingForm.companyName?.trim?.() || '') : '',
      taxId: billingForCompany ? (billingForm.taxId?.trim?.() || '') : '',
      companyRegistrationNumber: billingForCompany ? (billingForm.companyRegistrationNumber?.trim?.() || '') : '',
      address: {
        line1: billingForm.address?.trim?.() || '',
        line2: billingForm.address2?.trim?.() || '',
        city: billingForm.city?.trim?.() || '',
        state: selectedState?.name || billingForm.state || '',
        postalCode: billingForm.zip?.trim?.() || '',
        country: selectedCountry?.isoCode || billingForm.country || ''
      }
    };
  }

  private startStripeCheckout(
    paymentId: string,
    selectedPlanLevel: string,
    options?: {
      cancelStripeSubscriptionOnSuccess?: string | null;
      previousAppSubscriptionId?: string | null;
    }
  ): void {
    const promoStripeSyncError = this.getAppliedPromoStripeSyncError();
    if (promoStripeSyncError) {
      this.error = promoStripeSyncError;
      this.processing = false;
      return;
    }

    const accountId = this.accountContextService.activeAccountIdSignal();
    const priceId = this.planDetails?.stripePriceId;

    if (!accountId || !priceId || !this.planDetails) {
      this.error = !this.planDetails
        ? 'Selected plan could not be loaded for checkout.'
        : `Stripe checkout is not configured for "${this.planDetails.name}" yet. Add a Stripe Price ID to this plan in Admin > Subscription Plans.`;
      this.processing = false;
      return;
    }

    const checkoutEmail = this.resolveCheckoutEmail();
    const customerName = this.resolveCheckoutCustomerName();
    const billingDetails = this.buildStripeBillingDetails();

    this.stripeCheckoutService.createCheckoutSession({
      priceId,
      accountId,
      userId: this.userId,
      userEmail: checkoutEmail,
      customerName,
      billingDetails,
      planLevel: selectedPlanLevel,
      planName: this.planDetails.name,
      subscriptionId: this.subscriptionId,
      paymentId,
      cancelStripeSubscriptionOnSuccess: options?.cancelStripeSubscriptionOnSuccess ?? null,
      previousAppSubscriptionId: options?.previousAppSubscriptionId ?? null,
      promotionCode: this.appliedPromo?.code ?? null
    }).subscribe({
      next: response => {
        window.location.assign(response.url);
      },
      error: err => {
        console.error('Failed to create Stripe checkout session:', err);
        const modeLabel = this.appSettingsService.stripeLivePaymentsEnabled() ? 'live' : 'sandbox';
        const baseMessage = `Unable to start Stripe checkout. Please verify Stripe ${modeLabel} configuration and try again.`;
        if (!environment.production && err instanceof Error && err.message) {
          this.error = `${baseMessage} Details: ${err.message}`;
        } else {
          this.error = baseMessage;
        }
        this.processing = false;
      }
    });
  }
}

